import pshLogo from '../../assets/psh-logo.png';
import React from 'react';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';

const THEME_LOADERS = {
  halloween: {
    icon: '🎃',
    spinnerBorder: 'border-t-orange-500 border-r-purple-600 border-b-orange-400',
    title: 'Summoning Spooky Products...',
    subtitle: 'Brewing mystical scripts & mods from the crypt 👻',
    glowColor: 'rgba(249, 115, 22, 0.4)',
    titleColor: 'text-orange-500 dark:text-orange-400',
  },
  christmas: {
    icon: '🎄',
    spinnerBorder: 'border-t-red-500 border-r-green-500 border-b-yellow-400',
    title: 'Unwrapping Holiday Products...',
    subtitle: 'Gathering awesome gifts from Santa’s workshop ❄️',
    glowColor: 'rgba(220, 38, 38, 0.4)',
    titleColor: 'text-red-500 dark:text-red-400',
  },
  newyear: {
    icon: '🎆',
    spinnerBorder: 'border-t-amber-400 border-r-indigo-500 border-b-yellow-300',
    title: 'Launching 2026 Gaming Power...',
    subtitle: 'Counting down to unstoppable victories & elite deals 🥂',
    glowColor: 'rgba(245, 158, 11, 0.45)',
    titleColor: 'text-amber-500 dark:text-amber-400',
  },
  valentines: {
    icon: '💖',
    spinnerBorder: 'border-t-rose-500 border-r-pink-400 border-b-rose-300',
    title: 'Crafting Lovely Products for You...',
    subtitle: 'Infusing Cupid’s sweetest gaming blessings into your cart 🌹',
    glowColor: 'rgba(244, 63, 94, 0.45)',
    titleColor: 'text-rose-500 dark:text-rose-400',
  },
  diwali: {
    icon: '🪔',
    spinnerBorder: 'border-t-amber-400 border-r-red-600 border-b-yellow-400',
    title: 'Illuminating Awesome Products...',
    subtitle: 'May divine light, prosperity & fortune bless your shopping 🪔',
    glowColor: 'rgba(245, 158, 11, 0.55)',
    titleColor: 'text-amber-500 dark:text-amber-400',
  },
  default: {
    icon: '⚡',
    spinnerBorder: 'border-t-primary-500 border-r-secondary-400 border-b-primary-300',
    title: 'Loading awesome products...',
    subtitle: 'Preparing your elite scripts, mods & accessories 🚀',
    glowColor: 'rgba(59, 130, 246, 0.3)',
    titleColor: 'text-gray-900 dark:text-white',
  }
};

const LoadingSpinner = ({ size = 'md', color = 'primary' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  };

  const colorClasses = {
    primary: 'border-primary-500',
    secondary: 'border-secondary-500',
    white: 'border-white',
    gray: 'border-gray-500',
  };

  return (
    <div className="flex items-center justify-center">
      <motion.div
        className={`${sizeClasses[size]} border-4 border-transparent ${colorClasses[color]} border-t-current rounded-full`}
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
    </div>
  );
};

export const PageLoader = () => {
  const { activeTheme } = useSelector((state) => state.ui || {});
  const themeConfig = THEME_LOADERS[activeTheme] || THEME_LOADERS.default;

  return (
    <div className="fixed inset-0 bg-white/85 dark:bg-gray-950/90 backdrop-blur-md flex items-center justify-center z-50 select-none">
      <div className="text-center px-4 max-w-sm">
        {/* Animated Themed Center Avatar & Glowing Ring */}
        <div className="relative w-20 h-20 mx-auto mb-5 flex items-center justify-center">
          {/* Ambient Glow */}
          <div
            className="absolute inset-0 rounded-full blur-xl pointer-events-none"
            style={{ backgroundColor: themeConfig.glowColor }}
          />

          {/* Rotating Spinner Ring */}
          <motion.div
            className={`absolute inset-0 border-4 border-transparent ${themeConfig.spinnerBorder} rounded-full`}
            animate={{ rotate: 360 }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
          />

          {/* Themed Center Icon */}
          {activeTheme === 'default' ? (
            <motion.div
              className="relative w-10 h-10 rounded-xl overflow-hidden shadow-md ring-1 ring-white/20 select-none z-10"
              animate={{ scale: [1, 1.12, 1] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              <img src={pshLogo} alt="PSH" className="w-full h-full object-cover" />
            </motion.div>
          ) : (
            <motion.span
              className="text-3xl filter drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)] select-none z-10"
              animate={{ scale: [1, 1.15, 1] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              {themeConfig.icon}
            </motion.span>
          )}
        </div>

        {/* Welcoming Themed Title */}
        <motion.p
          className={`text-base md:text-lg font-bold tracking-tight ${themeConfig.titleColor}`}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {themeConfig.title}
        </motion.p>

        {/* Themed Subtitle */}
        <motion.p
          className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.4 }}
        >
          {themeConfig.subtitle}
        </motion.p>
      </div>
    </div>
  );
};

export default LoadingSpinner;
